import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../utils/Utils.js';
import ErrorMessage from '../shared/ErrorMessage.js';
import './ForgotPassword.css';

const ForgotPassword = (props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('email'); // 'email', 'code', 'password'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestCode = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    fetch(`${API_BASE}/api/auth/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ email })
    })
    .then(response => response.json())
    .then(data => {
      setIsLoading(false);
      if (data.success) {
        setSuccessMessage(t('reset_code_sent'));
        setStep('code');
      } else {
        setErrorMessage(data.message);
      }
    })
    .catch(error => {
      setIsLoading(false);
      setErrorMessage(error.message);
    });
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    fetch(`${API_BASE}/api/auth/verify-reset-code`, {
      method: 'POST',
      body: JSON.stringify({ email, code })
    })
    .then(response => response.json())
    .then(data => {
      setIsLoading(false);
      if (data.success) {
        setSuccessMessage('');
        setStep('password');
      } else {
        setErrorMessage(data.message);
      }
    })
    .catch(error => {
      setIsLoading(false);
      setErrorMessage(error.message);
    });
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage(t('passwords_do_not_match'));
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage(t('password_too_short'));
      return;
    }

    setIsLoading(true);

    fetch(`${API_BASE}/api/auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ email, code, new_password: newPassword })
    })
    .then(response => response.json())
    .then(data => {
      setIsLoading(false);
      if (data.success) {
        setSuccessMessage(t('password_reset_success'));
        setTimeout(() => {
          props.onLoginClick();
        }, 2000);
      } else {
        setErrorMessage(data.message);
      }
    })
    .catch(error => {
      setIsLoading(false);
      setErrorMessage(error.message);
    });
  };

  const renderEmailStep = () => (
    <form className="ForgotPasswordForm" onSubmit={handleRequestCode}>
      <p className="instructions">{t("forgot_password_instructions")}</p>
      <input
        name="email"
        type="email"
        placeholder={t("email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <button type="submit" disabled={isLoading}>
        {isLoading ? t("sending") : t("send_reset_code")}
      </button>
      <ErrorMessage message={errorMessage}/>
      <div className="links">
        <a href="#" onClick={props.onLoginClick}>{t("back_to_login")}</a>
      </div>
    </form>
  );

  const renderCodeStep = () => (
    <form className="ForgotPasswordForm" onSubmit={handleVerifyCode}>
      {successMessage && <p className="success-message">{successMessage}</p>}
      <p className="instructions">{t("enter_code_instructions")}</p>
      <input
        name="code"
        type="text"
        placeholder={t("verification_code")}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        maxLength={6}
        required
      />
      <button type="submit" disabled={isLoading}>
        {isLoading ? t("verifying") : t("verify_code")}
      </button>
      <ErrorMessage message={errorMessage}/>
      <div className="links">
        <a href="#" onClick={() => { setStep('email'); setErrorMessage(''); }}>
          {t("resend_code")}
        </a>
      </div>
    </form>
  );

  const renderPasswordStep = () => (
    <form className="ForgotPasswordForm" onSubmit={handleResetPassword}>
      {successMessage && <p className="success-message">{successMessage}</p>}
      <input
        name="newPassword"
        type="password"
        placeholder={t("new_password")}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
      />
      <input
        name="confirmPassword"
        type="password"
        placeholder={t("confirm_new_password")}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
      />
      <button type="submit" disabled={isLoading}>
        {isLoading ? t("saving") : t("reset_password")}
      </button>
      <ErrorMessage message={errorMessage}/>
    </form>
  );

  switch (step) {
    case 'code':
      return renderCodeStep();
    case 'password':
      return renderPasswordStep();
    default:
      return renderEmailStep();
  }
};

export default ForgotPassword;
